/**
 * Caregiver clinical forms — body check, vomiting, daily activity,
 * hospital transfer, appointments, and critical incident reports
 * (injury + medication).
 *
 * Each form is small, self-contained, and POSTs to its backend endpoint.
 * Shared via {@link CaregiverFormsPanel} which is mounted into WellnessPanel.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ChevronDown, ChevronUp, Plus, X, Sparkles,
  ClipboardList, Droplet, ListChecks, Ambulance, CalendarClock, AlertOctagon,
} from "lucide-react";
import VoiceIncidentModal from "@/components/VoiceIncidentModal";

/* ---------------- Helpers ---------------- */
function Section({ title, icon: Icon, accent, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-surface overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 hover:bg-clinical-bg/40 transition"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ background: accent + "1a", color: accent }}
          >
            <Icon size={16} />
          </span>
          <span className="font-medium text-clinical-text">{title}</span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-clinical-border/40 pt-3">{children}</div>}
    </div>
  );
}

const PrimaryBtn = ({ children, disabled, onClick, testid, tone = "accent" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full rounded-lg py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 ${
      tone === "danger" ? "bg-red-600" : "bg-clinical-accent"
    }`}
    data-testid={testid}
  >
    {disabled ? <Loader2 size={14} className="inline animate-spin" /> : children}
  </button>
);

/* ============== Body Check ============== */
const BODY_AREAS = [
  { v: "head", l: "Head" }, { v: "face", l: "Face" }, { v: "neck", l: "Neck" },
  { v: "chest", l: "Chest" }, { v: "back", l: "Back" }, { v: "abdomen", l: "Abdomen" },
  { v: "left_arm", l: "L. Arm" }, { v: "right_arm", l: "R. Arm" },
  { v: "left_hand", l: "L. Hand" }, { v: "right_hand", l: "R. Hand" },
  { v: "buttocks", l: "Buttocks" },
  { v: "left_leg", l: "L. Leg" }, { v: "right_leg", l: "R. Leg" },
  { v: "left_foot", l: "L. Foot" }, { v: "right_foot", l: "R. Foot" },
  { v: "genital_perineum", l: "Genital/perineum" },
];
const FINDINGS = [
  { v: "normal", l: "Normal", color: "#22C55E" },
  { v: "redness", l: "Redness", color: "#F59E0B" },
  { v: "bruise", l: "Bruise", color: "#A16207" },
  { v: "cut", l: "Cut", color: "#EF4444" },
  { v: "rash", l: "Rash", color: "#EC4899" },
  { v: "swelling", l: "Swelling", color: "#F59E0B" },
  { v: "pressure_sore", l: "Pressure sore", color: "#B91C1C" },
  { v: "wound", l: "Wound", color: "#B91C1C" },
  { v: "scab", l: "Scab", color: "#92400E" },
  { v: "other", l: "Other", color: "#64748B" },
];

function BodyCheckForm({ clientId, onSaved }) {
  const [findings, setFindings] = useState([]);
  const [integrity, setIntegrity] = useState("intact");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const addRow = () => setFindings((f) => [...f, { area: "head", finding: "normal", note: "" }]);
  const updateRow = (i, k, v) => setFindings((f) => f.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setFindings((f) => f.filter((_, j) => j !== i));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/body-check`, {
        findings, overall_skin_integrity: integrity, notes,
      });
      toast.success("Body check saved");
      setFindings([]); setNotes("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="bc-integrity" className="label-eyebrow">Overall skin integrity</label>
        <select
          id="bc-integrity"
          value={integrity}
          onChange={(e) => setIntegrity(e.target.value)}
          className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
          data-testid="body-check-integrity"
        >
          <option value="intact">Intact</option>
          <option value="concerns">Concerns</option>
          <option value="compromised">Compromised</option>
        </select>
      </div>
      <div className="space-y-2">
        {findings.map((row, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
            <select
              value={row.area}
              onChange={(e) => updateRow(i, "area", e.target.value)}
              className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-xs"
              data-testid={`body-check-area-${i}`}
            >
              {BODY_AREAS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
            </select>
            <select
              value={row.finding}
              onChange={(e) => updateRow(i, "finding", e.target.value)}
              className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-xs"
              data-testid={`body-check-finding-${i}`}
            >
              {FINDINGS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
            </select>
            <button
              type="button" onClick={() => removeRow(i)}
              className="rounded-lg border border-clinical-border p-1.5 text-clinical-subtext hover:text-red-600"
              aria-label="Remove finding"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          type="button" onClick={addRow}
          className="w-full rounded-lg border border-dashed border-clinical-border py-1.5 text-xs text-clinical-subtext hover:text-clinical-text"
          data-testid="body-check-add-finding"
        >
          <Plus size={12} className="inline mr-1" /> Add finding
        </button>
      </div>
      <input
        type="text"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid="body-check-notes"
      />
      <PrimaryBtn onClick={submit} disabled={saving} testid="body-check-save">Save body check</PrimaryBtn>
    </div>
  );
}

/* ============== Vomiting ============== */
function VomitingForm({ clientId, onSaved }) {
  const [color, setColor] = useState("clear");
  const [volume, setVolume] = useState("small");
  const [containsBlood, setContainsBlood] = useState(false);
  const [containsFood, setContainsFood] = useState(false);
  const [trigger, setTrigger] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/vomiting`, {
        color, volume, contains_blood: containsBlood, contains_food: containsFood, trigger,
      });
      toast.success("Vomiting episode logged");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="vom-color" className="label-eyebrow">Colour</label>
          <select id="vom-color" value={color} onChange={(e) => setColor(e.target.value)}
            className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="vomit-color">
            <option value="clear">Clear</option>
            <option value="yellow">Yellow (bile)</option>
            <option value="green">Green (bile)</option>
            <option value="brown">Brown</option>
            <option value="red">Red ⚠ (blood)</option>
            <option value="coffee_grounds">Coffee grounds ⚠</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="vom-volume" className="label-eyebrow">Volume</label>
          <select id="vom-volume" value={volume} onChange={(e) => setVolume(e.target.value)}
            className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="vomit-volume">
            <option value="small">Small</option>
            <option value="moderate">Moderate</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={containsBlood} onChange={(e) => setContainsBlood(e.target.checked)}
            data-testid="vomit-blood" />
          Contains blood
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={containsFood} onChange={(e) => setContainsFood(e.target.checked)}
            data-testid="vomit-food" />
          Contains food
        </label>
      </div>
      <input type="text" value={trigger} onChange={(e) => setTrigger(e.target.value)}
        placeholder="Trigger / context (optional)"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid="vomit-trigger" />
      <PrimaryBtn onClick={submit} disabled={saving} testid="vomit-save">Save episode</PrimaryBtn>
    </div>
  );
}

/* ============== Daily Activity ============== */
const ADL_OPTIONS = ["independent", "assist", "total_care", "refused"];
const ADL_LABEL = { independent: "Independent", assist: "Assist", total_care: "Total care", refused: "Refused" };
const ADLS = [
  { k: "bathing", l: "Bathing" }, { k: "dressing", l: "Dressing" },
  { k: "grooming", l: "Grooming" }, { k: "toileting", l: "Toileting" },
  { k: "eating", l: "Eating" },
];
const ACTIVITY_PRESETS = ["walk", "tv", "puzzles", "music", "visitors", "outdoors", "reading", "exercise"];

function DailyActivityForm({ clientId, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    activity_date: today,
    bathing: "assist", dressing: "assist", grooming: "assist",
    toileting: "assist", eating: "assist",
    social_engagement: "moderate", appetite: "good",
    activities: [], notes: "",
  });
  const [saving, setSaving] = useState(false);
  const toggleAct = (a) => setForm((f) => ({
    ...f, activities: f.activities.includes(a) ? f.activities.filter((x) => x !== a) : [...f.activities, a],
  }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/daily-activity`, form);
      toast.success("Daily activity logged");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2">
        {ADLS.map((adl) => (
          <div key={adl.k} className="flex items-center justify-between gap-2">
            <span className="text-xs text-clinical-subtext">{adl.l}</span>
            <select
              value={form[adl.k]}
              onChange={(e) => setForm({ ...form, [adl.k]: e.target.value })}
              className="rounded-lg border border-clinical-border bg-white px-2 py-1 text-xs"
              data-testid={`daily-${adl.k}`}
            >
              {ADL_OPTIONS.map((o) => <option key={o} value={o}>{ADL_LABEL[o]}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="da-engagement" className="label-eyebrow">Social engagement</label>
          <select id="da-engagement" value={form.social_engagement}
            onChange={(e) => setForm({ ...form, social_engagement: e.target.value })}
            className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="daily-engagement">
            <option value="none">None</option><option value="low">Low</option>
            <option value="moderate">Moderate</option><option value="high">High</option>
          </select>
        </div>
        <div>
          <label htmlFor="da-appetite" className="label-eyebrow">Appetite</label>
          <select id="da-appetite" value={form.appetite}
            onChange={(e) => setForm({ ...form, appetite: e.target.value })}
            className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="daily-appetite">
            <option value="poor">Poor</option><option value="fair">Fair</option><option value="good">Good</option>
          </select>
        </div>
      </div>
      <div>
        <div className="label-eyebrow mb-1">Activities done</div>
        <div className="flex flex-wrap gap-1.5">
          {ACTIVITY_PRESETS.map((a) => (
            <button key={a} type="button" onClick={() => toggleAct(a)}
              className={`rounded-md px-2 py-1 text-xs font-medium border transition ${
                form.activities.includes(a)
                  ? "border-clinical-accent bg-clinical-accent/10 text-clinical-accent"
                  : "border-clinical-border bg-white text-clinical-subtext"
              }`}
              data-testid={`daily-activity-${a}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
      <PrimaryBtn onClick={submit} disabled={saving} testid="daily-save">Save daily activity</PrimaryBtn>
    </div>
  );
}

/* ============== Hospital Transfer ============== */
const DOC_PRESETS = ["MAR", "advance_directive", "med_list", "code_status", "id", "insurance_card"];

function HospitalTransferForm({ clientId, onSaved }) {
  const [form, setForm] = useState({
    reason: "", transfer_type: "ER", transport_method: "911",
    receiving_facility: "", receiving_facility_phone: "",
    systolic: "", diastolic: "", pulse: "", temp_f: "", o2_sat: "", pain_level: "",
    belongings: "", documents_sent: [], family_notified: false, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDoc = (d) => setForm((f) => ({
    ...f, documents_sent: f.documents_sent.includes(d) ? f.documents_sent.filter((x) => x !== d) : [...f.documents_sent, d],
  }));

  const submit = async () => {
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["systolic", "diastolic", "pulse", "o2_sat", "pain_level"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      payload.temp_f = payload.temp_f === "" ? null : Number(payload.temp_f);
      await api.post(`/clients/${clientId}/hospital-transfers`, payload);
      toast.success("Hospital transfer logged");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <textarea value={form.reason} onChange={(e) => set("reason", e.target.value)}
        rows={2} placeholder="Reason for transfer*"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        required data-testid="transfer-reason" />
      <div className="grid grid-cols-2 gap-2">
        <select value={form.transfer_type} onChange={(e) => set("transfer_type", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
          aria-label="Transfer type" data-testid="transfer-type">
          <option value="ER">ER</option><option value="scheduled_admission">Scheduled admission</option>
          <option value="psych">Psychiatric</option><option value="other">Other</option>
        </select>
        <select value={form.transport_method} onChange={(e) => set("transport_method", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
          aria-label="Transport method" data-testid="transfer-transport">
          <option value="911">911</option><option value="private_ambulance">Private ambulance</option>
          <option value="family">Family</option><option value="self">Self</option><option value="other">Other</option>
        </select>
      </div>
      <input type="text" value={form.receiving_facility} onChange={(e) => set("receiving_facility", e.target.value)}
        placeholder="Receiving facility"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid="transfer-facility" />
      <div className="grid grid-cols-3 gap-2 text-xs">
        <input type="number" placeholder="Systolic" value={form.systolic}
          onChange={(e) => set("systolic", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5"
          aria-label="Systolic blood pressure" data-testid="transfer-sys" />
        <input type="number" placeholder="Diastolic" value={form.diastolic}
          onChange={(e) => set("diastolic", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5"
          aria-label="Diastolic blood pressure" data-testid="transfer-dia" />
        <input type="number" placeholder="Pulse" value={form.pulse}
          onChange={(e) => set("pulse", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5"
          aria-label="Pulse" data-testid="transfer-pulse" />
        <input type="number" step="0.1" placeholder="Temp °F" value={form.temp_f}
          onChange={(e) => set("temp_f", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5"
          aria-label="Temperature" data-testid="transfer-temp" />
        <input type="number" placeholder="O2 %" value={form.o2_sat}
          onChange={(e) => set("o2_sat", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5"
          aria-label="O2 saturation" data-testid="transfer-o2" />
        <input type="number" min="0" max="10" placeholder="Pain 0-10" value={form.pain_level}
          onChange={(e) => set("pain_level", e.target.value)}
          className="rounded-lg border border-clinical-border bg-white px-2 py-1.5"
          aria-label="Pain level" data-testid="transfer-pain" />
      </div>
      <input type="text" value={form.belongings} onChange={(e) => set("belongings", e.target.value)}
        placeholder="Belongings sent (e.g. glasses, dentures, wallet)"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid="transfer-belongings" />
      <div>
        <div className="label-eyebrow mb-1">Documents sent</div>
        <div className="flex flex-wrap gap-1.5">
          {DOC_PRESETS.map((d) => (
            <button key={d} type="button" onClick={() => toggleDoc(d)}
              className={`rounded-md px-2 py-1 text-xs font-medium border transition ${
                form.documents_sent.includes(d)
                  ? "border-clinical-accent bg-clinical-accent/10 text-clinical-accent"
                  : "border-clinical-border bg-white text-clinical-subtext"
              }`}
              data-testid={`transfer-doc-${d}`}
            >{d.replace(/_/g, " ")}</button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={form.family_notified}
          onChange={(e) => set("family_notified", e.target.checked)}
          data-testid="transfer-family-notified" />
        Family notified
      </label>
      <PrimaryBtn onClick={submit} disabled={saving} tone="danger" testid="transfer-save">
        Log transfer
      </PrimaryBtn>
    </div>
  );
}

/* ============== Appointments (list + add) ============== */
const APPT_TYPE_LABEL = {
  primary_care: "Primary care", specialist: "Specialist", dental: "Dental",
  vision: "Vision", psychiatric: "Psychiatric", lab: "Lab", imaging: "Imaging",
  physical_therapy: "Physical therapy", occupational_therapy: "Occupational therapy",
  social_services: "Social services", other: "Other",
};

function AppointmentsList({ clientId }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    appointment_at: "", type: "primary_care",
    provider_name: "", provider_phone: "", location: "", reason: "",
    transport_required: false, transport_method: "family",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/appointments`, { params: { days_back: 30 } });
      setItems(data);
    } catch { /* swallow */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.appointment_at) { toast.error("Date/time required"); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/appointments`, form);
      toast.success("Appointment added");
      setAdding(false);
      setForm({ ...form, appointment_at: "", provider_name: "", reason: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const markCompleted = async (id) => {
    try {
      await api.patch(`/clients/${clientId}/appointments/${id}`, { status: "completed" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-2">
      {items.length === 0 && !adding && (
        <p className="text-xs text-clinical-subtext">No appointments yet.</p>
      )}
      {items.map((a) => (
        <div key={a.id} className="rounded-lg border border-clinical-border bg-white p-2.5 text-sm"
          data-testid={`appointment-${a.id}`}>
          <div className="flex items-center justify-between">
            <span className="font-medium text-clinical-text">
              {APPT_TYPE_LABEL[a.type] || a.type}{a.provider_name ? ` · ${a.provider_name}` : ""}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
              a.status === "completed" ? "bg-green-100 text-green-700"
              : a.status === "missed" ? "bg-red-100 text-red-700"
              : "bg-blue-100 text-blue-700"
            }`}>{a.status}</span>
          </div>
          <div className="text-xs text-clinical-subtext mt-0.5">
            {new Date(a.appointment_at).toLocaleString()}
            {a.reason && ` · ${a.reason}`}
          </div>
          {a.status === "scheduled" && (
            <button type="button" onClick={() => markCompleted(a.id)}
              className="mt-1 text-xs text-clinical-accent hover:underline"
              data-testid={`appointment-complete-${a.id}`}>
              Mark completed
            </button>
          )}
        </div>
      ))}
      {adding ? (
        <div className="space-y-2 rounded-lg border border-clinical-border bg-clinical-bg/30 p-2.5">
          <input type="datetime-local" value={form.appointment_at}
            onChange={(e) => setForm({ ...form, appointment_at: e.target.value })}
            className="w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-xs"
            aria-label="Appointment date and time" data-testid="appt-datetime" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-xs"
            aria-label="Appointment type" data-testid="appt-type">
            {Object.entries(APPT_TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input type="text" placeholder="Provider name" value={form.provider_name}
            onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
            className="w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-xs"
            data-testid="appt-provider" />
          <input type="text" placeholder="Reason" value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            className="w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-xs"
            data-testid="appt-reason" />
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.transport_required}
              onChange={(e) => setForm({ ...form, transport_required: e.target.checked })} />
            Transport required
          </label>
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setAdding(false)}
              className="flex-1 rounded-lg border border-clinical-border py-1.5 text-xs">Cancel</button>
            <PrimaryBtn onClick={submit} disabled={saving} testid="appt-save">Save</PrimaryBtn>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          className="w-full rounded-lg border border-dashed border-clinical-border py-1.5 text-xs text-clinical-subtext hover:text-clinical-text"
          data-testid="appointments-add">
          <Plus size={12} className="inline mr-1" /> Add appointment
        </button>
      )}
    </div>
  );
}

/* ============== Critical Incidents (Injury + Medication) ============== */
function CriticalIncidentForm({ clientId, kind, onClose, onSaved }) {
  const isInjury = kind === "injury";
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(
    isInjury ? {
      location: "", description: "",
      injury_mechanism: "cut", injury_severity: "minor", body_part: "",
      witnessed: false, first_aid_given: false, photo_taken: false,
      er_transferred: false, family_notified: false, manager_notified: false,
    } : {
      location: "", description: "",
      med_name: "", med_dose: "", med_error_type: "missed_dose",
      pharmacy_contacted: false, doctor_contacted: false,
      family_notified: false, manager_notified: false,
    }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Description required"); return; }
    if (!isInjury && !form.med_name.trim()) { toast.error("Medication name required"); return; }
    setSaving(true);
    try {
      const path = isInjury ? "injury" : "medication";
      await api.post(`/clients/${clientId}/incidents/${path}`, form);
      toast.success(`${isInjury ? "Injury" : "Medication"} incident reported`);
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid={`ci-form-${kind}`}>
      <input type="text" value={form.location} onChange={(e) => set("location", e.target.value)}
        placeholder="Where did it happen?"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid={`ci-${kind}-location`} />
      <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
        rows={3} placeholder="Describe what happened*"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid={`ci-${kind}-description`} />

      {isInjury ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.injury_mechanism} onChange={(e) => set("injury_mechanism", e.target.value)}
              className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
              aria-label="Injury mechanism" data-testid="ci-injury-mechanism">
              <option value="cut">Cut</option><option value="burn">Burn</option>
              <option value="bruise">Bruise</option><option value="abrasion">Abrasion</option>
              <option value="puncture">Puncture</option><option value="sprain">Sprain</option>
              <option value="fracture">Fracture</option><option value="bite">Bite</option>
              <option value="other">Other</option>
            </select>
            <select value={form.injury_severity} onChange={(e) => set("injury_severity", e.target.value)}
              className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
              aria-label="Injury severity" data-testid="ci-injury-severity">
              <option value="none">No injury</option><option value="minor">Minor</option>
              <option value="moderate">Moderate</option><option value="severe">Severe</option>
            </select>
          </div>
          <input type="text" value={form.body_part} onChange={(e) => set("body_part", e.target.value)}
            placeholder="Body part affected"
            className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
            data-testid="ci-injury-body-part" />
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.witnessed} onChange={(e) => set("witnessed", e.target.checked)} />
              Witnessed
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.first_aid_given} onChange={(e) => set("first_aid_given", e.target.checked)} />
              First aid given
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.photo_taken} onChange={(e) => set("photo_taken", e.target.checked)} />
              Photo taken
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.er_transferred} onChange={(e) => set("er_transferred", e.target.checked)} />
              ER transferred
            </label>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={form.med_name} onChange={(e) => set("med_name", e.target.value)}
              placeholder="Medication name*"
              className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
              data-testid="ci-med-name" />
            <input type="text" value={form.med_dose} onChange={(e) => set("med_dose", e.target.value)}
              placeholder="Dose"
              className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
              data-testid="ci-med-dose" />
          </div>
          <select value={form.med_error_type} onChange={(e) => set("med_error_type", e.target.value)}
            className="w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            aria-label="Medication error type" data-testid="ci-med-error-type">
            <option value="missed_dose">Missed dose</option>
            <option value="wrong_dose">Wrong dose</option>
            <option value="wrong_time">Wrong time</option>
            <option value="wrong_med">Wrong medication</option>
            <option value="wrong_route">Wrong route</option>
            <option value="refused">Refused</option>
            <option value="duplicate">Duplicate dose</option>
            <option value="expired">Expired medication</option>
            <option value="other">Other</option>
          </select>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.pharmacy_contacted}
                onChange={(e) => set("pharmacy_contacted", e.target.checked)} />
              Pharmacy contacted
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.doctor_contacted}
                onChange={(e) => set("doctor_contacted", e.target.checked)} />
              Doctor contacted
            </label>
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.family_notified} onChange={(e) => set("family_notified", e.target.checked)} />
          Family notified
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.manager_notified} onChange={(e) => set("manager_notified", e.target.checked)} />
          Manager notified
        </label>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onClose}
          className="flex-1 rounded-lg border border-clinical-border py-2 text-sm font-medium"
          data-testid={`ci-${kind}-cancel`}>Cancel</button>
        <PrimaryBtn onClick={submit} disabled={saving} tone="danger" testid={`ci-${kind}-submit`}>
          Report {isInjury ? "injury" : "med incident"}
        </PrimaryBtn>
      </div>
    </div>
  );
}

/* ============== Master panel ============== */
export default function CaregiverFormsPanel({ clientId }) {
  const [ciKind, setCiKind] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [voiceIncidentOpen, setVoiceIncidentOpen] = useState(false);
  const bump = () => setRefresh((r) => r + 1);

  return (
    <div className="space-y-3" data-testid="caregiver-forms-panel">
      <Section title="Body check" icon={ListChecks} accent="#0EA5E9" testid="forms-body-check">
        <BodyCheckForm clientId={clientId} onSaved={bump} key={`bc-${refresh}`} />
      </Section>
      <Section title="Vomiting" icon={Droplet} accent="#A16207" testid="forms-vomiting">
        <VomitingForm clientId={clientId} onSaved={bump} key={`vom-${refresh}`} />
      </Section>
      <Section title="Daily activity" icon={ClipboardList} accent="#16A34A" testid="forms-daily-activity">
        <DailyActivityForm clientId={clientId} onSaved={bump} key={`da-${refresh}`} />
      </Section>
      <Section title="Hospital transfer" icon={Ambulance} accent="#B91C1C" testid="forms-hospital-transfer">
        <HospitalTransferForm clientId={clientId} onSaved={bump} key={`ht-${refresh}`} />
      </Section>
      <Section title="Appointments" icon={CalendarClock} accent="#7C3AED" testid="forms-appointments">
        <AppointmentsList clientId={clientId} key={`app-${refresh}`} />
      </Section>
      <div className="card-surface p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertOctagon size={16} className="text-red-600" />
            <span className="font-medium text-clinical-text">Critical incident report</span>
          </div>
          <button type="button" onClick={() => setVoiceIncidentOpen(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-clinical-accent px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"
            data-testid="voice-incident-open">
            <Sparkles size={12} /> Voice
          </button>
        </div>
        {ciKind ? (
          <CriticalIncidentForm
            clientId={clientId} kind={ciKind}
            onClose={() => setCiKind(null)} onSaved={bump}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setCiKind("injury")}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
              data-testid="open-injury-form">
              <Plus size={14} /> Injury
            </button>
            <button type="button" onClick={() => setCiKind("medication")}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
              data-testid="open-med-incident-form">
              <Plus size={14} /> Medication
            </button>
          </div>
        )}
      </div>
      <VoiceIncidentModal
        open={voiceIncidentOpen}
        clientId={clientId}
        onClose={() => setVoiceIncidentOpen(false)}
        onSaved={bump}
      />
    </div>
  );
}
